import { useState, useEffect, useRef, useCallback } from "react";
import { poolApi, parameterApi } from "../../services/poolService";
import { measurementApi } from "../../services/measurementService";

// Fallback display names for common parameter IDs returned by the measurements service
const PARAM_DISPLAY: Record<string, string> = {
  ph: "pH",
  chlorine: "Cloro Libre",
  totalChlorine: "Cloro Total",
  combinedChlorine: "Cloro Combinado",
  alkalinity: "Alcalinidad",
  hardness: "Dureza",
  cyanuricAcid: "Ácido Cianúrico",
  temperature: "Temperatura",
  conductivity: "Conductividad",
  turbidity: "Turbidez",
};

interface Alert {
  id: string;
  type: "critical" | "warning" | "info";
  poolName: string;
  parameter: string;
  parameterName: string;
  currentValue: number;
  optimalMin: number;
  optimalMax: number;
  unit: string;
  message: string;
  recommendation: string;
  timestamp: string;
  isRead: boolean;
}

interface AlertSystemProps {
  isOpen: boolean;
  onClose: () => void;
  onAlertCountChange: (count: number) => void;
}

export default function AlertSystem({
  isOpen,
  onClose,
  onAlertCountChange,
}: AlertSystemProps) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [filter, setFilter] = useState<"all" | "critical" | "warning" | "info">(
    "all",
  );
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Preserve read state across re-fetches
  const readIds = useRef<Set<string>>(new Set());

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const [pools, parameters] = await Promise.all([
        poolApi.getAll(),
        parameterApi.getAll().catch(() => []),
      ]);

      // Build lookup map: parameterId → PoolParameter definition
      const paramMap = new Map(parameters.map((p) => [p.parameterId, p]));

      // Fetch latest measurement per pool in parallel; ignore pools with no data
      const results = await Promise.allSettled(
        pools.map((pool) =>
          measurementApi
            .getLatestByPoolId(pool.id)
            .then((m) => ({ pool, measurement: m })),
        ),
      );

      const generatedAlerts: Alert[] = [];
      const now = new Date();

      for (const result of results) {
        if (result.status !== "fulfilled") continue;
        const { pool, measurement } = result.value;
        if (!measurement?.readings?.length) continue;

        for (const reading of measurement.readings) {
          if (!reading.status || reading.status === "OPTIMAL") continue;

          const paramDef = paramMap.get(reading.parameterId);
          const paramName =
            paramDef?.name ??
            PARAM_DISPLAY[reading.parameterId] ??
            reading.parameterId;
          const unit = reading.unit ?? paramDef?.unit ?? "";
          const optimalMin = paramDef?.optimalMin ?? 0;
          const optimalMax = paramDef?.optimalMax ?? 0;
          const timestamp = measurement.timestamp ?? now.toISOString();
          const type: Alert["type"] =
            reading.status === "CRITICAL" ? "critical" : "warning";

          let recommendation = `Verificar y ajustar ${paramName.toLowerCase()} a los valores recomendados`;
          if (paramDef) {
            recommendation =
              reading.value < paramDef.optimalMin
                ? `Aumentar ${paramName.toLowerCase()} hasta el rango óptimo (${paramDef.optimalMin}–${paramDef.optimalMax} ${unit})`
                : `Reducir ${paramName.toLowerCase()} hasta el rango óptimo (${paramDef.optimalMin}–${paramDef.optimalMax} ${unit})`;
          }

          const alertId = `${pool.id}-${reading.parameterId}-${measurement.id ?? timestamp}`;
          generatedAlerts.push({
            id: alertId,
            type,
            poolName: pool.name,
            parameter: reading.parameterId,
            parameterName: paramName,
            currentValue: reading.value,
            optimalMin,
            optimalMax,
            unit,
            message:
              type === "critical"
                ? `¡CRÍTICO! ${pool.name} – ${paramName}: ${reading.value} ${unit}`
                : `${pool.name} – ${paramName} fuera del rango óptimo: ${reading.value} ${unit}`,
            recommendation,
            timestamp,
            isRead: readIds.current.has(alertId),
          });
        }
      }

      if (generatedAlerts.length === 0) {
        generatedAlerts.push({
          id: `info-${now.getTime()}`,
          type: "info",
          poolName: "",
          parameter: "general",
          parameterName: "Estado General",
          currentValue: 0,
          optimalMin: 0,
          optimalMax: 0,
          unit: "",
          message: "Todos los parámetros están dentro del rango óptimo",
          recommendation:
            "Continuar con el monitoreo regular según el programa establecido",
          timestamp: now.toISOString(),
          isRead: false,
        });
      }

      setAlerts(generatedAlerts);
      setLastUpdated(new Date());
      onAlertCountChange(
        generatedAlerts.filter((a) => a.type !== "info" && !a.isRead).length,
      );
    } catch {
      // Silently fail – keep existing alerts visible
    } finally {
      setLoading(false);
    }
  }, [onAlertCountChange]);

  // Initial fetch + auto-refresh every 2 minutes
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const markAsRead = (alertId: string) => {
    readIds.current.add(alertId);
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId ? { ...alert, isRead: true } : alert,
      ),
    );
    onAlertCountChange(
      alerts.filter((a) => a.type !== "info" && !a.isRead && a.id !== alertId)
        .length,
    );
  };

  const markAllAsRead = () => {
    alerts.forEach((a) => readIds.current.add(a.id));
    setAlerts((prev) => prev.map((alert) => ({ ...alert, isRead: true })));
    onAlertCountChange(0);
  };

  const filteredAlerts = alerts.filter(
    (alert) => filter === "all" || alert.type === filter,
  );

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "critical":
        return "ri-alarm-warning-fill";
      case "warning":
        return "ri-alert-fill";
      case "info":
        return "ri-information-fill";
      default:
        return "ri-notification-3-fill";
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case "critical":
        return "bg-red-50 border-red-200 text-red-800";
      case "warning":
        return "bg-amber-50 border-amber-200 text-amber-800";
      case "info":
        return "bg-teal-50 border-teal-200 text-teal-800";
      default:
        return "bg-gray-50 border-gray-200 text-gray-800";
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case "critical":
        return "text-red-500";
      case "warning":
        return "text-amber-500";
      case "info":
        return "text-teal-500";
      default:
        return "text-gray-500";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-20 pr-4">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Panel de Alertas */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-notification-3-fill text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">
                  Centro de Alertas
                </h3>
                <p className="text-white/80 text-sm">
                  {alerts.filter((a) => a.type !== "info" && !a.isRead).length}{" "}
                  alertas activas
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <div className="flex space-x-2">
            {[
              { key: "all", label: "Todas" },
              { key: "critical", label: "Críticas" },
              { key: "warning", label: "Advertencias" },
              { key: "info", label: "Info" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key as typeof filter)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  filter === f.key
                    ? "bg-teal-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={markAllAsRead}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
          >
            Marcar todas leídas
          </button>
        </div>

        {/* Lista de Alertas */}
        <div className="max-h-96 overflow-y-auto">
          {loading && alerts.length === 0 ? (
            <div className="p-8 text-center">
              <i className="ri-loader-4-line text-4xl text-teal-500 mb-3 animate-spin"></i>
              <p className="text-gray-500 text-sm">Cargando alertas...</p>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="p-8 text-center">
              <i className="ri-checkbox-circle-fill text-4xl text-green-500 mb-3"></i>
              <p className="text-gray-600">No hay alertas en esta categoría</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 transition-colors ${alert.isRead ? "opacity-60" : ""} hover:bg-gray-50`}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        alert.type === "critical"
                          ? "bg-red-100"
                          : alert.type === "warning"
                            ? "bg-amber-100"
                            : "bg-teal-100"
                      }`}
                    >
                      <i
                        className={`${getAlertIcon(alert.type)} text-xl ${getIconColor(alert.type)}`}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAlertColor(alert.type)}`}
                        >
                          {alert.type === "critical"
                            ? "Crítico"
                            : alert.type === "warning"
                              ? "Advertencia"
                              : "Información"}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatTimestamp(alert.timestamp)}
                        </span>
                      </div>
                      {alert.poolName && (
                        <p className="text-xs text-teal-600 font-medium mb-0.5">
                          <i className="ri-water-flash-line mr-1"></i>
                          {alert.poolName}
                        </p>
                      )}
                      <p className="text-sm font-medium text-gray-800 mb-1">
                        {alert.message}
                      </p>
                      {alert.type !== "info" && (
                        <div className="bg-gray-50 rounded-lg p-2 mt-2">
                          <p className="text-xs text-gray-600">
                            <i className="ri-lightbulb-line mr-1 text-amber-500"></i>
                            <strong>Recomendación:</strong>{" "}
                            {alert.recommendation}
                          </p>
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <span className="mr-3">
                              <i className="ri-ruler-line mr-1"></i>
                              Actual:{" "}
                              <strong>
                                {alert.currentValue} {alert.unit}
                              </strong>
                            </span>
                            <span>
                              <i className="ri-focus-3-line mr-1"></i>
                              Óptimo:{" "}
                              <strong>
                                {alert.optimalMin}-{alert.optimalMax}{" "}
                                {alert.unit}
                              </strong>
                            </span>
                          </div>
                        </div>
                      )}
                      {!alert.isRead && alert.type !== "info" && (
                        <button
                          onClick={() => markAsRead(alert.id)}
                          className="mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-check-line mr-1"></i>
                          Marcar como leída
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <button
              onClick={fetchAlerts}
              disabled={loading}
              className="flex items-center gap-1 text-teal-600 hover:text-teal-700 font-medium cursor-pointer disabled:opacity-50"
            >
              <i
                className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}
              ></i>
              {loading ? "Actualizando..." : "Actualizar"}
            </button>
            <span>
              <i className="ri-time-line mr-1"></i>
              {lastUpdated
                ? `Actualizado: ${lastUpdated.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`
                : "Actualizando..."}
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
